import {
  abbreviateId,
  BlotterTable,
  BlotterTableExtrasMenu,
  Box,
  ButtonVariants,
  CurrencyIcon,
  Divider,
  Flex,
  FormControlSizes,
  getShowJSONContextItem,
  HStack,
  IconButton,
  IconName,
  IndicatorBadge,
  IndicatorBadgeSizes,
  LoaderTalos,
  ModeEnum,
  OrderFormSides,
  QuoteStatus,
  Text,
  Toggle,
  ToggleHorizontal,
  type ToggleHorizontalOption,
  Tooltip,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useSecurity,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, GridOptions } from 'ag-grid-community';
import { compact } from 'lodash-es';

import { getTreeRowBlotterGroupColDef } from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { RfqBestQuoteByToggle } from '../../../../components/OMS/RfqBestQuoteByToggle';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import { BottomActionBar } from './BottomActionBar';
import type { QuoteBreakdownRow } from './rows';
import { useQuoteBreakdownColumns } from './useQuoteBreakdownColumns';
import { useQuoteBreakdownObs } from './useQuoteBreakdownObs';

const SIDE_OPTIONS: ToggleHorizontalOption<OrderFormSides>[] = [
  OrderFormSides.Twoway,
  OrderFormSides.Buy,
  OrderFormSides.Sell,
].map(side => ({
  value: side,
  displayName: side,
}));

interface TakerQuoteBreakdownBlotterProps {
  rfqID: string | undefined;
}

export const TakerQuoteBreakdownBlotter = ({ rfqID }: TakerQuoteBreakdownBlotterProps) => {
  const [shownSide, setShownSide] = useState<OrderFormSides>(OrderFormSides.Twoway);
  const {
    showAllDecimalsInBlotters,
    takerQuoteBreakdownShowAllDecimals,
    setTakerQuoteBreakdownShowAllDecimals,
    rfqBestQuoteBy,
  } = useTradingSettings();
  const { quote, dataObservable, errorObservable } = useQuoteBreakdownObs({ rfqID, bestBy: rfqBestQuoteBy });
  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const security = useSecurity(quote?.Symbol);

  const { jsonModal, handleClickJson } = useJsonModal();
  const defaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<QuoteBreakdownRow>) => {
    return compact([getShowJSONContextItem({ params, handleClickJson }), ...defaultContextMenuItems(params)]);
  });

  const columns = useQuoteBreakdownColumns({
    shownSide,
    sizeCounteringEnabled: quote?.NegotiationParameters?.SizeCountering === ModeEnum.Enabled,
  });
  const showAllDecimals = showAllDecimalsInBlotters || takerQuoteBreakdownShowAllDecimals;
  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const gridOptions: GridOptions<QuoteBreakdownRow> = useMemo(() => {
    return {
      getContextMenuItems,
      groupDisplayType: 'singleColumn',
      treeData: true,
      getDataPath: row => row.dataPath,
      groupDefaultExpanded: -1, // everything starts expanded
      autoGroupColumnDef: {
        headerName: 'Dealer',
        width: 200,
        editable: false,
        suppressColumnsToolPanel: false,
        cellRendererParams: {
          suppressCount: true,
        },
        sortable: false,
        ...getTreeRowBlotterGroupColDef(),
      },
    };
  }, [getContextMenuItems]);

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable,
    gridOptions,
    rowID: 'rowID' satisfies keyof QuoteBreakdownRow,
    columns,
    settings: blotterTableSettings,
  });

  // Quote.Currency is not yet typed as maybe undefined, which it can be going forward
  const currency: string | undefined = quote?.Currency;

  if (!quote) {
    return <LoaderTalos />;
  }

  return (
    <>
      <Flex flexDirection="column" h="100%">
        {/* Header part */}
        <HStack justifyContent="space-between" h="40px" alignItems="center" px="spacing12" style={{ flexShrink: 0 }}>
          {/* Left side header */}
          <HStack color="colorTextImportant" gap="spacing4">
            {currency && <CurrencyIcon currency={currency} />}
            <HStack gap="spacing4" alignItems="baseline">
              <Text fontSize="fontSizeLg">{quote.Symbol}</Text>
              <Text color="colorTextDefault">{security?.Description}</Text>
            </HStack>
          </HStack>

          {/* Right side header */}
          <HStack gap="spacing6">
            <ToggleHorizontal
              value={shownSide}
              onChange={setShownSide}
              options={SIDE_OPTIONS}
              size={FormControlSizes.Small}
            />

            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={() => blotterTable.expandAllGroups()}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={() => blotterTable.collapseAllGroups()}
              data-testid="collapse-all-button"
            />
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                label="Show All Decimals"
                size={FormControlSizes.Small}
                checked={showAllDecimals}
                disabled={showAllDecimalsInBlotters}
                onChange={setTakerQuoteBreakdownShowAllDecimals}
                data-testid="show-decimals-toggle"
              />
              <RfqBestQuoteByToggle size={FormControlSizes.Small} />
            </BlotterTableExtrasMenu>

            <Divider orientation="vertical" />

            <IndicatorBadge size={IndicatorBadgeSizes.Large}>
              <Tooltip tooltip={quote.RFQID}>
                <Text>RFQ ID: #{abbreviateId(quote.RFQID)}</Text>
              </Tooltip>
            </IndicatorBadge>
            <QuoteStatus quoteStatus={quote.QuoteStatus} />
          </HStack>
        </HStack>

        <Divider color="backgroundDivider" />

        <Box w="100%" h="100%" flex="1">
          <BlotterTable {...blotterTable} />
        </Box>

        <BottomActionBar quote={quote} />
      </Flex>
      {jsonModal}
    </>
  );
};
